/* eslint-disable no-loop-func */
import * as React from "react";
import { accessRightsStore } from "../Stores/AccessRightsStore";
import { Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { contactsStore } from "Stores/ContactsStore";
import { availableAccessRightsStore } from "../Stores/AvailableAccessRightsStore";
import { Table } from "Components/General/Table";
import { formatUsername } from "utils/formatUsername";

interface AccessRightsUserTableProps extends WithTranslation {}

interface AccessRightsUsersTableItem {
  name: string;
  accessLevel: API_ACCESSRIGHTSENTRY_ACCESSLEVEL;
  label: string[];
  key: string;
}

interface AccessRightsUserTableState {
  data: AccessRightsUsersTableItem[];
}

export const AccessRightsUserTable = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<AccessRightsUserTableProps, AccessRightsUserTableState> {
      private rightsLabelMap: { [key: string]: { color: string; title: string } } = {
        conflict: { color: "#ff8145", title: "Conflict" },
        inherited: { color: "#0000FF", title: "Inherited" },
        projectAdmin: { color: "#228B22", title: "Admin" },
      };

      constructor(props: AccessRightsUserTableProps) {
        super(props);
        this.state = {
          data: [],
        };
      }

      componentDidMount() {
        (async () => {
          await accessRightsStore.fetchPerUser();
          this.parseAccessRights(accessRightsStore.rightsPerUser);
        })();
      }

      parseAccessRights = (rights: API_AccessUserRight[]) => {
        const usersByGroup = {},
          users = [];
        let data = [];

        /**
         * Get uniq group id & users without groups
         */
        rights.forEach((item, rightIdx) => {
          const { userId, conflict, inherited, right, projectAdmin } = item;
          if (item.groupId) {
            const groupId = item.groupId;
            if (usersByGroup[groupId]) {
              usersByGroup[groupId].push({ rightIdx, userId, conflict, inherited, projectAdmin, right });
            } else {
              usersByGroup[groupId] = [{ rightIdx, userId, conflict, inherited, projectAdmin, right }];
            }
          } else {
            users.push({ rightIdx, userId, conflict, inherited, projectAdmin, right });
          }
        });

        /**
         * Assign group users
         */
        for (const groupKey in usersByGroup) {
          if (Object.prototype.hasOwnProperty.call(usersByGroup, groupKey)) {
            const foundGroupMembers: API_GroupMember[] = groupListStore.list.find(
              (group) => group.id === +groupKey
            ).groupMembers;

            usersByGroup[groupKey].forEach((item) => {
              data = data.concat(
                foundGroupMembers
                  .filter((member) => member.id === item.userId)
                  .map((user) => ({
                    name: user.fullName,
                    accessLevel: this.getAccessByRight(item.right),
                    label: this.getLabelByRight(item),
                    key: `group${groupKey}-user${user.id}-${item.rightIdx}`,
                  }))
              );
            });
          }
        }

        /**
         * Assign single users
         */
        users.forEach((user) => {
          data = data.concat(
            contactsStore.projectUsers
              .filter((contact) => contact.id === user.userId)
              .map((person) => ({
                name: formatUsername({ firstName: person.firstName, surname: person.surname }),
                accessLevel: this.getAccessByRight(user.right),
                label: this.getLabelByRight(user),
                key: `user${person.id}-${user.rightIdx}`,
              }))
          );
        });

        this.setState({ data });
      };

      getAccessByRight = (right: API_ACCESSRIGHTSENTRY_ACCESSLEVEL) => {
        const val = availableAccessRightsStore.data.find((item) => item.value === right);
        return val ? val.key : "";
      };

      getLabelByRight = ({ conflict, inherited, projectAdmin }) => {
        const labels = [];
        const conditions = { conflict, inherited, projectAdmin };
        for (const key in conditions) {
          if (Object.prototype.hasOwnProperty.call(conditions, key) && conditions[key]) {
            labels.push(key);
          }
        }
        return labels;
      };

      render() {
        const { t } = this.props;
        const { data } = this.state;

        const columns: ColumnsType<AccessRightsUsersTableItem> = [
          {
            dataIndex: "name",
            title: t("Username"),
          },
          {
            dataIndex: "accessLevel",
            title: t("Access right"),
          },
          {
            dataIndex: "label",
            title: t("Note"),
            render: (text, record: AccessRightsUsersTableItem) => {
              return record.label.map((label, idx) => {
                const { color, title } = this.rightsLabelMap[label];
                return (
                  <Tag key={idx} color={color} variant="solid">
                    {t(title)}
                  </Tag>
                );
              });
            },
          },
        ];

        return <Table<AccessRightsUsersTableItem> name="docAccessRights" columns={columns} dataSource={data} />;
      }
    }
  )
);
